import * as PIXI from 'pixi.js';
import type { MapObject } from '../../../models/mapObject';
import type { MapMarkerContainer } from './mapPixiExtensions';
import { saleTagTexture } from './mapBadgeTextures';

// ── Shared map-marker PIXI builders ──
// Extracted verbatim from MapCanvas (decomposition step 04). These builders are
// pure factories that take only PIXI data / refs, so they are shared between the
// map-objects render effect (map/useMapObjectsRender.ts) and MapCanvas's own
// incremental-add / createObjectMarker code — imported by both, duplicated in
// neither (rule 4a: no back-import from MapCanvas).

export const createDebugPoints = (container: PIXI.Container, spriteWidth: number, spriteHeight: number, anchorX: number, anchorY: number) => {
  return;
  const centerX = -(anchorX - 0.5) * spriteWidth;
  const centerY = -(anchorY - 0.5) * spriteHeight;
  const topY = -anchorY * spriteHeight;
  const bottomY = (1 - anchorY) * spriteHeight;
  const leftX = -anchorX * spriteWidth;
  const rightX = (1 - anchorX) * spriteWidth;
  const middleBottomY = (centerY + bottomY) / 2;

  // Add debug points
  const pointRadius = 5;
  const createGreenPoint = (x: number, y: number) => {
    const point = new PIXI.Graphics();
    point.circle(x, y, pointRadius).fill(0x00ff00);
    return point;
  };
  const createOrangePoint = (x: number, y: number) => {
    const point = new PIXI.Graphics();
    point.circle(x, y, pointRadius).fill(0xff8800);
    return point;
  };
  const createBlackPoint = (x: number, y: number) => {
    const point = new PIXI.Graphics();
    point.circle(x, y, pointRadius).fill(0x000000);
    return point;
  };
  const createPinkPoint = (x: number, y: number) => {
    const point = new PIXI.Graphics();
    point.circle(x, y, pointRadius).fill(0xff00ff);
    return point;
  };
  const createBrownPoint = (x: number, y: number) => {
    const point = new PIXI.Graphics();
    point.circle(x, y, pointRadius).fill(0x8B4513);
    return point;
  };

  const topYAbove = topY - 20; // Slightly above top edge
  container.addChild(createBrownPoint(centerX, topYAbove)); // Above top
  container.addChild(createGreenPoint(centerX, topY));    // Top edge
  container.addChild(createBlackPoint(centerX, bottomY)); // Bottom edge
  container.addChild(createGreenPoint(leftX, centerY));    // Left edge
  container.addChild(createGreenPoint(rightX, centerY));   // Right edge
  container.addChild(createGreenPoint(centerX, centerY)); // Center
  container.addChild(createOrangePoint(centerX, middleBottomY)); // Between center and bottom
  container.addChild(createPinkPoint(centerX, middleBottomY)); // Between center and bottom (variant)
};

/**
 * Opacity for the informational overlays drawn above houses — the "for sale" plaque
 * and the owner avatar. They are secondary to the buildings themselves, so they sit
 * back rather than competing with the map art.
 */
export const MAP_OVERLAY_ALPHA = 0.6;

/**
 * The "for sale" plaque drawn above a house (green) or a decor object like the
 * lighthouse (custom colour).
 *
 * 🔴 Single source of truth on purpose. This markup existed twice — once in
 * useMapObjectsRender (initial build) and once in useMarkerVisibility (rebuild when
 * listings arrive, which is the common path since listings load after the houses).
 * The copies drifted: one showed "S" at 0.6 alpha while the other still drew the
 * full price at full opacity and silently overwrote it. Build the plaque here only.
 *
 * Houses listed by a player show a plain "S" rather than the asking price — the
 * numbers cluttered the map.
 */
export const createSaleTag = (
  mapObject: MapObject,
  tagY: number,
  opts?: { label?: string; bgColor?: number; showPlots?: boolean },
): PIXI.Container => {
  const container = new PIXI.Container();
  // One shared texture per label/colour (render/mapBadgeTextures.ts), not a Text each.
  const tag = saleTagTexture(opts?.label ?? 'S', opts?.bgColor ?? 0x1a8c3b);
  const plaque = new PIXI.Sprite(tag.texture);
  plaque.anchor.set(0.5);
  plaque.width = tag.width;
  plaque.height = tag.height;
  container.addChild(plaque);
  container.alpha = MAP_OVERLAY_ALPHA;
  container.position.set(mapObject.x, tagY);
  container.zIndex = mapObject.y + 1000;
  container.eventMode = 'none';
  container.cullable = true;
  container.visible = opts?.showPlots ?? true;
  return container;
};

/**
 * Build the free-plot marker.
 *
 * 🔴 `plotMarkerTexture` is deliberately NON-nullable. This used to accept
 * `Texture | null` and fall back to a green debug circle, which is exactly how the
 * "free plot turns into a green dot after leaving and re-entering the map" bug
 * reached players: unmount frees map textures and nulls the singleton, while
 * `isPixiAppReady` is a one-way latch that is never reset, so the marker build could
 * legitimately observe null. Callers must obtain the texture via
 * `ensurePlotMarkerTexture()` (see ../mapScene) — the type now makes the broken
 * state unrepresentable rather than merely unlikely. Do not reintroduce a fallback.
 */
export const createPlotIcon = (
  mapObject: MapObject,
  plotMarkerTexture: PIXI.Texture,
): PIXI.Container => {
  const container = new PIXI.Container();

  const sprite = new PIXI.Sprite(plotMarkerTexture);
  // Зменшуємо на 20% відносно природного розміру (87x110)
  sprite.scale.set(0.8);
  sprite.anchor.set(0.5, 1.0);
  container.addChild(sprite);
  createDebugPoints(container, sprite.width, sprite.height, 0.5, 1.0);

  container.position.set(mapObject.x, mapObject.y);
  container.zIndex = mapObject.y;
  container.eventMode = 'static';
  container.cursor = 'pointer';
  (container as MapMarkerContainer).mapObjectData = mapObject;
  container.cullable = true;

  return container;
};
